import { controlTriggerVariants, menuItemVariants } from '@/components/ui/button-variants';
import { ChevronDown } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/cn';

export type SelectOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

type Props = {
  value: string;
  onValueChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** Dark sidebar trigger styling */
  tone?: 'default' | 'sidebar';
  'aria-label'?: string;
};

export function Select({
  value,
  onValueChange,
  options,
  placeholder = '—',
  disabled,
  className,
  tone = 'default',
  'aria-label': ariaLabel
}: Props) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [panelStyle, setPanelStyle] = useState<{ top: number; left: number; width: number } | null>(
    null
  );
  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const listId = useId();

  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    if (!open || !rootRef.current) return;
    const rect = rootRef.current.getBoundingClientRect();
    setPanelStyle({
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width
    });
    const idx = Math.max(
      0,
      options.findIndex((o) => o.value === value)
    );
    setHighlight(idx >= 0 ? idx : 0);
  }, [open, options, value]);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      const target = e.target as Node;
      if (
        !rootRef.current?.contains(target) &&
        !listRef.current?.contains(target)
      ) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function pick(idx: number) {
    const opt = options[idx];
    if (!opt || opt.disabled) return;
    onValueChange(opt.value);
    setOpen(false);
  }

  function onTriggerKeyDown(e: React.KeyboardEvent) {
    if (disabled) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setOpen((o) => !o);
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) setOpen(true);
      else setHighlight((h) => Math.min(h + 1, options.length - 1));
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) setOpen(true);
      else setHighlight((h) => Math.max(h - 1, 0));
    }
    if (e.key === 'Home') {
      e.preventDefault();
      setHighlight(0);
    }
    if (e.key === 'End') {
      e.preventDefault();
      setHighlight(options.length - 1);
    }
  }

  function onListKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, options.length - 1));
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      pick(highlight);
    }
    if (e.key === 'Home') {
      e.preventDefault();
      setHighlight(0);
    }
    if (e.key === 'End') {
      e.preventDefault();
      setHighlight(options.length - 1);
    }
  }

  const panel =
    open && panelStyle
      ? createPortal(
          <ul
            ref={listRef}
            id={listId}
            role="listbox"
            tabIndex={-1}
            onKeyDown={onListKeyDown}
            className="fixed z-[250] max-h-60 overflow-auto rounded-[var(--fv-radius)] border border-[var(--fv-border)] bg-[var(--fv-surface-elevated)] py-1 shadow-[var(--fv-shadow-md)]"
            style={{
              top: panelStyle.top,
              left: panelStyle.left,
              width: panelStyle.width
            }}
          >
            {options.map((opt, i) => (
              <li
                key={opt.value}
                role="option"
                aria-selected={opt.value === value}
                className={menuItemVariants({
                  highlight: i === highlight,
                  selected: opt.value === value,
                  disabled: opt.disabled
                })}
                onMouseEnter={() => setHighlight(i)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(i);
                }}
              >
                {opt.label}
              </li>
            ))}
          </ul>,
          document.body
        )
      : null;

  return (
    <div ref={rootRef} className={cn('relative inline-block min-w-[8rem]', className)}>
      <button
        type="button"
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        className={controlTriggerVariants({ tone, open })}
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={onTriggerKeyDown}
      >
        <span
          className={cn(
            'truncate',
            !selected &&
              (tone === 'sidebar' ? 'text-[var(--fv-sidebar-muted)]' : 'text-[var(--fv-text-muted)]')
          )}
        >
          {selected?.label ?? placeholder}
        </span>
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 transition-transform',
            tone === 'sidebar' ? 'text-[var(--fv-sidebar-muted)]' : 'text-[var(--fv-text-muted)]',
            open && 'rotate-180'
          )}
          aria-hidden
        />
      </button>
      {panel}
    </div>
  );
}
